import {
  ContactType,
  ContractLegalForm,
  InvoicingFrequency,
} from "shared/types/rental-contracts";
import { ExtractedContractDto } from "./types";

/** Product shape used by the add-contract modal (selectedProducts state). */
export interface MappedProduct {
  name: string;
  basePrice: number;
  indexedPrice: number;
  templateId: number | null;
  ledger: string | number | null;
  indexable: boolean;
  odooProductVariantId: number | null;
  odooTemplateName: string | null;
  archived: boolean;
  buildingAttributeId: number | null;
  vatRate: number;
}

/** Contract-contact row shape used by the add-contract modal (contractContacts state). */
export interface MappedContractContact {
  contactType: ContactType | null;
  resourceId: number | null;
  resource: any;
  wasExisting?: boolean;
  residentData?: any;
}

export interface MappedExtractedContract {
  products: MappedProduct[];
  priceInputValues: Record<string, string>;
  contractContacts: MappedContractContact[];
  signDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  legalForm: ContractLegalForm | null;
  invoicingFrequency: InvoicingFrequency | null;
  propertyTaxes: string | null;
  numResidents: number | null;
  fileLogId: number;
}

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Transform the backend extraction result into the exact state objects the
 * add-contract modal uses. Pure (no side effects) so it can be unit-tested and
 * the component just applies the result via its setters.
 */
export function mapExtractedContract(
  data: ExtractedContractDto,
): MappedExtractedContract {
  const products: MappedProduct[] = (data.products || []).map((p) => ({
    // Prefer the matched template name so the product dropdown resolves it;
    // fall back to the human label when no template matched (user re-selects).
    name: p.odooTemplateName || p.label,
    basePrice: p.amount,
    // Indexed rent is NOT in the base contract — leave it blank so the user
    // computes it via the Statbel rent calculator (see IndexedRentNotice).
    indexedPrice: 0,
    templateId: p.templateId,
    ledger: p.ledger,
    indexable: p.indexable,
    odooProductVariantId: p.odooProductVariantId,
    odooTemplateName: p.odooTemplateName,
    archived: false,
    buildingAttributeId: p.buildingAttributeId ?? null,
    vatRate: p.vatRate ?? 0,
  }));

  const priceInputValues: Record<string, string> = {};
  products.forEach((p, index) => {
    priceInputValues[`${index}-basePrice`] = String(p.basePrice);
    // Blank so the indexed-rent field shows empty (must be filled via Statbel).
    priceInputValues[`${index}-indexedPrice`] = "";
  });

  const contractContacts: MappedContractContact[] = (data.contacts || []).map(
    (c) => ({
      contactType: c.contactType ?? ContactType.TENANT,
      resourceId: c.resourceId,
      // `resource.resourceId` = the Contact DB id (matches the shape of
      // SearchComponent-picked resources), so the card can open /contact/:id.
      resource: { id: c.resourceId, resourceId: String(c.contactId), name: c.name },
      wasExisting: c.wasExisting,
      residentData: {
        email: c.email,
        phoneNumber: c.phone,
      },
    }),
  );

  return {
    products,
    priceInputValues,
    contractContacts,
    signDate: parseDate(data.signDate),
    startDate: parseDate(data.startDate),
    endDate: parseDate(data.endDate),
    legalForm: data.legalForm ?? null,
    invoicingFrequency: data.invoicingFrequency ?? null,
    propertyTaxes: data.propertyTaxes ?? null,
    numResidents:
      typeof data.numResidents === "number" && data.numResidents > 0
        ? data.numResidents
        : null,
    fileLogId: data.fileLogId,
  };
}
